/** Stealth skill: movement-while-hidden with a tile-step budget, per-step XP, and proximity checks. */
import { ConcealmentSkillEffect } from './ConcealmentSkillEffect';
import type { ConcealmentCallbacks, ConcealmentTuning } from './ConcealmentSkillEffect';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { TILE_SIZE } from '../../utils/Constants';
import { DebugLog } from '../../debug/DebugLog';
import { DebugConfig } from '../../debug/DebugConfig';

export interface StealthCallbacks extends ConcealmentCallbacks {
  isHideActive: () => boolean;
  activateHide: () => void;
  isPlayerStationary: () => boolean;
  getHideCooldown: () => number;
  onStealthBreak: () => void;
  getSpecterXp: () => number;
  wipeAggroInRadius: (radiusPx: number) => void;
}

/** SkillEffect for the Stealth ability: movement-while-hidden with a tile-step budget. */
export class StealthSkillEffect extends ConcealmentSkillEffect {
  readonly skillId = 'stealth';
  declare protected callbacks: StealthCallbacks;
  private _distanceMoved = 0;
  private _maxSteps = 0;

  constructor(callbacks: StealthCallbacks) {
    super(callbacks);
  }

  protected getTuning(): ConcealmentTuning {
    return ConfigLoader.stealthTuning;
  }

  protected getCooldownMs(): number {
    return ConfigLoader.stealthTuning.cooldownMs;
  }

  protected canActivateExtra(): boolean {
    // If Hide is already active, Stealth can layer on top.
    // If Hide isn't active but player is stationary, Stealth will force-activate Hide on success.
    if (this.callbacks.isHideActive()) return true;
    return this.callbacks.isPlayerStationary();
  }

  protected onActivateSuccess(): void {
    // If Hide isn't active, force-activate it — the shared LOS check + roll already passed
    if (!this.callbacks.isHideActive()) {
      this.callbacks.activateHide();
    }
    this._distanceMoved = 0;
    const config = ConfigLoader.stealthTuning;
    this._maxSteps = Math.floor(config.baseSteps + config.stepsPerXp * this.callbacks.getXp());

    // Aggro wipe — radius scales with Specter tree XP
    const aggroWipe = config.aggroWipe;
    const specterXp = this.callbacks.getSpecterXp();
    const t = Math.min(specterXp / aggroWipe.xpForMaxRadius, 1);
    const radius = aggroWipe.minRadiusPx + (aggroWipe.maxRadiusPx - aggroWipe.minRadiusPx) * t;
    this.callbacks.wipeAggroInRadius(radius);
  }

  protected shouldBreakOnUpdate(_delta: number): boolean {
    // If hide deactivated underneath us, break stealth
    return !this.callbacks.isHideActive();
  }

  protected onBreakConceal(): void {
    // Break Hide first (sets Hide's cooldown), then inherit it.
    // When Stealth is an evolution replacing Hide in the hotbar, the player
    // sees Stealth's cooldown — so it must match Hide's exponential curve.
    this.callbacks.onStealthBreak();
    const hideCd = this.callbacks.getHideCooldown();
    if (hideCd > 0) {
      this.cooldownRemaining = hideCd;
    }
  }

  /** Accumulate movement distance against the step budget; awards fractional XP per step and breaks when exhausted. */
  addMovementDistance(pixels: number): void {
    if (!this._isActive) return;
    const prevSteps = Math.floor(this._distanceMoved / TILE_SIZE);
    this._distanceMoved += pixels;
    const newSteps = Math.floor(this._distanceMoved / TILE_SIZE);

    // Award fractional XP per step: total from full budget = 1x baseXpPerUse
    if (newSteps > prevSteps && this._maxSteps > 0) {
      this.callbacks.onXpGained(this.skillId, {
        stepFraction: 1 / this._maxSteps,
        enemyHasLOS: this.callbacks.anyEnemyHasLOS(),
        dangerPresent: this.callbacks.getDanger() > 0,
      });
    }

    if (this.stepsRemaining <= 0 && !DebugConfig.flags.zeroCooldowns) {
      DebugLog.log('skills', `stealth: step budget exhausted (${this._maxSteps} steps)`);
      this.breakStealth();
    }
  }

  get stepsRemaining(): number {
    const used = Math.floor(this._distanceMoved / TILE_SIZE);
    return Math.max(0, this._maxSteps - used);
  }

  get maxSteps(): number {
    return this._maxSteps;
  }

  /** Force-break Stealth from external callers. */
  breakStealth(): void {
    this.breakConceal();
  }
}
